import SitePage from "@/components/application/site-page";
import ValidationErrors from "@/components/inertia/validation-errors";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { complaints_path } from "@/lib/routes";
import { useForm, usePage } from "@inertiajs/react";
import { AlertCircleIcon, CheckCircle2Icon } from "lucide-react";
import { FormEvent, ReactNode } from "react";
import useTranslations from "@/hooks/use-translations";

function ComplaintForm() {
  const { flash } = usePage().props;
  const { t } = useTranslations("complaints.new");

  const form = useForm({
    complaint_book_entry: {
      claim_type: "claim",
      full_name: "",
      document_number: "",
      email: "",
      phone: "",
      address: "",
      is_minor: false,
      guardian_full_name: "",
      contracted_good: "",
      claim_amount_cents: "",
      detail: "",
      consumer_request: "",
    },
  });

  function updateField<K extends keyof typeof form.data.complaint_book_entry>(
    field: K,
    value: (typeof form.data.complaint_book_entry)[K],
  ) {
    form.setData((values) => ({
      complaint_book_entry: { ...values.complaint_book_entry, [field]: value },
    }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    form.post(complaints_path(), {
      onSuccess() {
        form.reset();
      },
    });
  }

  const { complaint_book_entry: data } = form.data;

  function errorsFor(field: keyof typeof data) {
    return form.errors[`complaint_book_entry.${field}`];
  }

  return (
    <form onSubmit={onSubmit} className="not-prose flex flex-col gap-6">
      {flash.alert && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>{flash.alert}</AlertTitle>
        </Alert>
      )}

      {flash.notice && (
        <Alert>
          <CheckCircle2Icon />
          <AlertTitle>{flash.notice}</AlertTitle>
        </Alert>
      )}

      <Field>
        <FieldLabel>{t("claim_type_label")}</FieldLabel>
        <RadioGroup
          value={data.claim_type}
          onValueChange={(value) => updateField("claim_type", value)}
          className="flex gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="claim" id="claim_type_claim" />
            <Label htmlFor="claim_type_claim">
              {t("claim_type_claim")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="complaint" id="claim_type_complaint" />
            <Label htmlFor="claim_type_complaint">
              {t("claim_type_complaint")}
            </Label>
          </div>
        </RadioGroup>
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field data-invalid={!!errorsFor("full_name")}>
          <FieldLabel htmlFor="full_name">{t("full_name_label")}</FieldLabel>
          <Input
            id="full_name"
            value={data.full_name}
            onChange={(event) => updateField("full_name", event.target.value)}
            aria-invalid={!!errorsFor("full_name")}
          />
          <ValidationErrors errors={errorsFor("full_name")} />
        </Field>

        <Field data-invalid={!!errorsFor("document_number")}>
          <FieldLabel htmlFor="document_number">
            {t("document_number_label")}
          </FieldLabel>
          <Input
            id="document_number"
            value={data.document_number}
            onChange={(event) =>
              updateField("document_number", event.target.value)
            }
            aria-invalid={!!errorsFor("document_number")}
          />
          <ValidationErrors errors={errorsFor("document_number")} />
        </Field>

        <Field data-invalid={!!errorsFor("email")}>
          <FieldLabel htmlFor="email">{t("email_field_label")}</FieldLabel>
          <Input
            id="email"
            type="email"
            value={data.email}
            onChange={(event) => updateField("email", event.target.value)}
            aria-invalid={!!errorsFor("email")}
          />
          <ValidationErrors errors={errorsFor("email")} />
        </Field>

        <Field data-invalid={!!errorsFor("phone")}>
          <FieldLabel htmlFor="phone">{t("phone_label")}</FieldLabel>
          <Input
            id="phone"
            value={data.phone}
            onChange={(event) => updateField("phone", event.target.value)}
            aria-invalid={!!errorsFor("phone")}
          />
          <ValidationErrors errors={errorsFor("phone")} />
        </Field>
      </div>

      <Field data-invalid={!!errorsFor("address")}>
        <FieldLabel htmlFor="address">{t("address_label")}</FieldLabel>
        <Input
          id="address"
          value={data.address}
          onChange={(event) => updateField("address", event.target.value)}
          aria-invalid={!!errorsFor("address")}
        />
        <ValidationErrors errors={errorsFor("address")} />
      </Field>

      <Field orientation="horizontal">
        <Checkbox
          id="is_minor"
          checked={data.is_minor}
          onCheckedChange={(checked) => updateField("is_minor", checked === true)}
        />
        <FieldLabel htmlFor="is_minor">{t("is_minor_label")}</FieldLabel>
      </Field>

      {data.is_minor && (
        <Field data-invalid={!!errorsFor("guardian_full_name")}>
          <FieldLabel htmlFor="guardian_full_name">
            {t("guardian_full_name_label")}
          </FieldLabel>
          <Input
            id="guardian_full_name"
            value={data.guardian_full_name}
            onChange={(event) =>
              updateField("guardian_full_name", event.target.value)
            }
            aria-invalid={!!errorsFor("guardian_full_name")}
          />
          <ValidationErrors errors={errorsFor("guardian_full_name")} />
        </Field>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <Field data-invalid={!!errorsFor("contracted_good")}>
          <FieldLabel htmlFor="contracted_good">
            {t("contracted_good_label")}
          </FieldLabel>
          <Input
            id="contracted_good"
            value={data.contracted_good}
            onChange={(event) =>
              updateField("contracted_good", event.target.value)
            }
            aria-invalid={!!errorsFor("contracted_good")}
          />
          <ValidationErrors errors={errorsFor("contracted_good")} />
        </Field>

        <Field data-invalid={!!errorsFor("claim_amount_cents")}>
          <FieldLabel htmlFor="claim_amount">
            {t("claim_amount_label")}
          </FieldLabel>
          <Input
            id="claim_amount"
            type="number"
            min="0"
            step="0.01"
            value={data.claim_amount_cents}
            onChange={(event) =>
              updateField("claim_amount_cents", event.target.value)
            }
            aria-invalid={!!errorsFor("claim_amount_cents")}
          />
          <ValidationErrors errors={errorsFor("claim_amount_cents")} />
        </Field>
      </div>

      <Field data-invalid={!!errorsFor("detail")}>
        <FieldLabel htmlFor="detail">{t("detail_label")}</FieldLabel>
        <Textarea
          id="detail"
          rows={4}
          value={data.detail}
          onChange={(event) => updateField("detail", event.target.value)}
          aria-invalid={!!errorsFor("detail")}
        />
        <ValidationErrors errors={errorsFor("detail")} />
      </Field>

      <Field data-invalid={!!errorsFor("consumer_request")}>
        <FieldLabel htmlFor="consumer_request">
          {t("consumer_request_label")}
        </FieldLabel>
        <Textarea
          id="consumer_request"
          rows={4}
          value={data.consumer_request}
          onChange={(event) =>
            updateField("consumer_request", event.target.value)
          }
          aria-invalid={!!errorsFor("consumer_request")}
        />
        <ValidationErrors errors={errorsFor("consumer_request")} />
      </Field>

      <Button type="submit" size="lg" disabled={form.processing}>
        {t("submit")}
      </Button>
    </form>
  );
}

function ComplaintsPage() {
  const { t } = useTranslations("complaints.new");

  return (
    <>
      <h1>Libro de Reclamaciones</h1>
      <p>{t("intro")}</p>
      <p dangerouslySetInnerHTML={{ __html: t("free_notice_html") }} />

      <table className="not-prose w-full text-left text-sm mb-8">
        <tbody>
          <tr className="border-b">
            <th className="py-2 pr-4 font-medium">
              {t("business_name_label")}
            </th>
            <td className="py-2">Gatito Pirata Technologies EIRL</td>
          </tr>
          <tr className="border-b">
            <th className="py-2 pr-4 font-medium">{t("ruc_label")}</th>
            <td className="py-2">20616175221</td>
          </tr>
          <tr className="border-b">
            <th className="py-2 pr-4 font-medium">{t("country_label")}</th>
            <td className="py-2">Perú</td>
          </tr>
          <tr>
            <th className="py-2 pr-4 font-medium">{t("email_label")}</th>
            <td className="py-2">brickgestor@gatitopirata.com</td>
          </tr>
        </tbody>
      </table>

      <ComplaintForm />
    </>
  );
}

ComplaintsPage.layout = (page: ReactNode) => <SitePage children={page} />;

export default ComplaintsPage;
